import { inject, Injectable } from '@angular/core';
import { CheckoutActions, CheckoutApiService } from '@checkout-v2/data-access';
import { MyShopService } from '@my-shop/feature';
import { Actions, createEffect, EffectNotification, ofType, OnRunEffects } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ShopActions } from '@shop/data-access';
import { from, Observable, of } from 'rxjs';
import { catchError, exhaustMap, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { PickupService, ReceiptService, UserPickupShopsService } from '../services';

@Injectable()
export class PickupShopsEffects implements OnRunEffects {
  private readonly actions$ = inject(Actions);
  private readonly checkoutApiService = inject(CheckoutApiService);
  private readonly pickupService = inject(PickupService);
  private readonly receiptTypeService = inject(ReceiptService);
  private readonly userPickupShopsService = inject(UserPickupShopsService);
  private readonly myShopService = inject(MyShopService);

  // Загрузить список магазинов самовывоза
  // Отправить запрос на бекенд
  loadUserShopList$: Observable<
    CheckoutActions.LoadUserPickupShopListSuccess | CheckoutActions.LoadUserPickupShopListFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.LOAD_USER_PICKUP_SHOP_LIST),
      switchMap(() =>
        this.checkoutApiService.loadUserShopList().pipe(
          map((shops) => new CheckoutActions.LoadUserPickupShopListSuccess(shops)),
          catchError((error) => of(new CheckoutActions.LoadUserPickupShopListFail(error))),
        ),
      ),
    ),
  );

  // Выбран способ получения "самовывоз"
  // Загрузить список адресов самовывоза
  loadAddressListAfterSetReceiptType$: Observable<ShopActions.LoadShopList | CheckoutActions.LoadUserPickupShopList> =
    createEffect(() =>
      this.actions$.pipe(
        ofType<CheckoutActions.LoadReceiptTypeSuccess>(CheckoutActions.LOAD_RECEIPT_TYPE_SUCCESS),
        switchMap(() => this.receiptTypeService.getActiveReceiptTypeId()),
        filter((activeReceiptType) => activeReceiptType === 'pickup'),
        switchMap(() => [new ShopActions.LoadShopList(), new CheckoutActions.LoadUserPickupShopList()]),
      ),
    );

  // Выбран способ получения не равный "самовывоз"
  // Сбросить список адресов самовывоза
  resetAddressListAfterSetReceiptType$: Observable<CheckoutActions.LoadUserPickupShopListReset> = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.LoadReceiptTypeSuccess>(CheckoutActions.LOAD_RECEIPT_TYPE_SUCCESS),
      switchMap(() => this.receiptTypeService.getActiveReceiptTypeId()),
      filter((activeReceiptType) => activeReceiptType !== 'pickup'),
      concatLatestFrom(() => this.userPickupShopsService.listState$),
      filter(([, state]) => !!state.error || !!state.success),
      map(() => new CheckoutActions.LoadUserPickupShopListReset()),
    ),
  );

  // -----------------------------------------

  // Сохранить магазин самовывоза
  addUserShop$: Observable<
    | CheckoutActions.AddUserPickupShopSuccess
    | CheckoutActions.AddUserPickupShopFail
    | CheckoutActions.LoadUserPickupShopListSuccess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.AddUserPickupShop>(CheckoutActions.ADD_USER_PICKUP_SHOP),
      map((action) => action.payload),
      switchMap(({ shopName }: { shopName: string }) =>
        this.checkoutApiService.addUserShop(shopName).pipe(
          switchMap((shops) =>
            from([
              new CheckoutActions.AddUserPickupShopSuccess({ shopName }),
              new CheckoutActions.LoadUserPickupShopListSuccess(shops),
            ]),
          ),
          catchError((error) => of(new CheckoutActions.AddUserPickupShopFail({ shopName, error }))),
        ),
      ),
    ),
  );

  // Удалить сохраненный магазин самовывоза
  removeUserShop$: Observable<
    | CheckoutActions.RemoveUserPickupShopSuccess
    | CheckoutActions.RemoveUserPickupShopFail
    | CheckoutActions.LoadUserPickupShopListSuccess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.RemoveUserPickupShop>(CheckoutActions.REMOVE_USER_PICKUP_SHOP),
      map((action) => action.payload),
      switchMap(({ shopName }) =>
        this.checkoutApiService.removeUserShop(shopName).pipe(
          switchMap((shops) =>
            from([
              new CheckoutActions.RemoveUserPickupShopSuccess({ shopName }),
              new CheckoutActions.LoadUserPickupShopListSuccess(shops),
            ]),
          ),
          catchError((error) => of(new CheckoutActions.RemoveUserPickupShopFail({ shopName, error }))),
        ),
      ),
    ),
  );

  // -----------------------------------------

  // Загружен список магазинов города
  // Установить или сбросить активный магазин самовывоза
  setActiveShopAfterLoadList$: Observable<CheckoutActions.SetShop> = createEffect(() =>
    this.actions$.pipe(
      ofType<ShopActions.LoadShopListSuccess>(ShopActions.LOAD_SHOPS_LIST_SUCCESS),
      map((action) => action.payload),
      concatLatestFrom(() => [this.myShopService.activeShop$, this.pickupService.activeShopId$]),
      map(([shopsList, myShop, activePickupShopId]) => {
        const current: string | undefined = activePickupShopId;
        let next: string | undefined;

        if (shopsList.length > 0) {
          let found;

          if (myShop) {
            found = shopsList.find((shop) => shop.name === myShop.name);
          }

          if (!found && activePickupShopId) {
            found = shopsList.find((shop) => shop.name === activePickupShopId);
          }

          next = found ? found.name : shopsList[0].name;
        }

        return [current, next];
      }),
      filter(([current, next]) => current !== next),
      map(([, next]) => new CheckoutActions.SetShop(next)),
    ),
  );

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>): Observable<EffectNotification> {
    return this.actions$.pipe(
      ofType(CheckoutActions.CHECKOUT_INIT),
      exhaustMap(() => resolvedEffects$.pipe(takeUntil(this.actions$.pipe(ofType(CheckoutActions.CHECKOUT_DESTROY))))),
    );
  }
}
